import axios from 'axios';
import { ContentMenuItem, ContentMenuResponse } from '@/types/contentMenu';
import { MenuGroup, MenuItem } from '@/config/menus';

/**
 * Fetches dynamic menus from the API
 */
export async function fetchContentMenus(unitId: string = '1'): Promise<ContentMenuItem[]> {
  try {
    const response = await axios.post<ContentMenuResponse>('/api/doServices', {
      method: 'GET',
      endpoint: `web/contentNested?unit_id=${unitId}&order=orders ASC`
    });

    if (response.data && response.data.data) {
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching dynamic menus:', error);
    return [];
  }
}

/**
 * Converts a ContentMenuItem to the internal MenuItem format
 */
function mapToMenuItem(item: ContentMenuItem, language: 'id' | 'en'): MenuItem {
  const label = language === 'en' ? item.title_en : item.title_idn;
  const isExternal = item.url && item.url.trim() !== '';
  const href = isExternal 
    ? item.url 
    : `/halaman/${item.slug}`;

  const menuItem: MenuItem = {
    id: item.slug || item.id.toString(),
    label: label || item.title_idn,
    href: href,
    external: isExternal,
  };

  // If has children, map them recursively
  if (item.children && item.children.length > 0) {
    menuItem.children = item.children.map(child => mapToMenuItem(child, language));
  }

  return menuItem;
}

/**
 * Flattens children into a flat MenuItem list (for items with grandchildren)
 */
function flattenToMenuItems(item: ContentMenuItem, language: 'id' | 'en'): MenuItem[] {
  const items: MenuItem[] = [];
  
  if (item.children && item.children.length > 0) {
    item.children.forEach(child => {
      items.push(mapToMenuItem(child, language));
    });
  } else {
    // No children, add the item itself as a link
    items.push(mapToMenuItem(item, language));
  }
  
  return items;
}

/**
 * Converts API content menus into a SINGLE mega menu
 * All top-level items become sections within one mega menu
 */
export function mapContentMenusToMenuGroups(
  contentMenus: ContentMenuItem[],
  language: 'id' | 'en' = 'id'
): MenuGroup[] {
  if (contentMenus.length === 0) {
    return [];
  }

  // Create one mega menu containing all API items as sections
  const megaMenu: MenuGroup = {
    id: 'menu-utama',
    label: language === 'en' ? 'Menu' : 'Menu',
    type: 'megamenu',
    columns: 4, // 4 columns for mega menu
    items: contentMenus.map(topLevelItem => {
      const label = language === 'en' ? topLevelItem.title_en : topLevelItem.title_idn;
      
      // Each top-level item becomes a section in the mega menu
      const section: MenuItem = {
        id: topLevelItem.slug || topLevelItem.id.toString(),
        label: label || topLevelItem.title_idn,
      };

      // If top-level item has children, they become the section's children
      if (topLevelItem.children && topLevelItem.children.length > 0) {
        section.children = topLevelItem.children.map(child => {
          const childItem = mapToMenuItem(child, language);
          // If child has its own children, keep them nested
          if (child.children && child.children.length > 0) {
            childItem.children = child.children.map(grandchild => mapToMenuItem(grandchild, language));
          }
          return childItem;
        });
      } else {
        // No children, create a link to the item itself
        const isExternal = topLevelItem.url && topLevelItem.url.trim() !== '';
        section.children = [{
          id: topLevelItem.slug || topLevelItem.id.toString(),
          label: label || topLevelItem.title_idn,
          href: isExternal ? topLevelItem.url : `/halaman/${topLevelItem.slug}`,
          external: isExternal,
        }];
      }

      return section;
    }),
  };

  return [megaMenu];
}
